import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../widgets/modal";
import TextField from "../../widgets/textField/textField";
import { BENCHMARK_SINGLECONTACT } from "../../constants/app.constants";
import {
  updateBenchmarkSingleContact,
  addBenchmarkSingleContact,
  fetchBenchmarkSingleContact,
} from "../overview/overview.utils";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import Checkbox from "../../widgets/checkbox";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";

type OverviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
};

const OverviewContactModal: FC<OverviewModalProps> = (props) => {
  const { userId, isOpen, onClose } = props;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [phoneOffice, setPhoneOffice] = useState("");
  const [phoneMobile, setPhoneMobile] = useState("");
  const [phoneOther, setPhoneOther] = useState("");
  const [emailOffice, setEmailOffice] = useState("");
  const [isPrimary, setPrimary] = useState(false);
  const [isBilling, setBilling] = useState(false);
  const [isShowLoader, setIsShowLoader] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");

  const orgIdNew = window.localStorage.getItem("ID");

  const { isFetching, data: dataBenchmarkSingleContact } = useQuery({
    queryKey: [BENCHMARK_SINGLECONTACT, userId],
    queryFn: () => fetchBenchmarkSingleContact(userId),
    enabled: !!userId,
    refetchOnWindowFocus: false,
  });

  const onChangeFieldValues = useCallback(
    (id: string, updatedValue: React.SetStateAction<string>) => {
      if (id == "firstName") {
        setFirstName(updatedValue);
      } else if (id == "lastName") {
        setLastName(updatedValue);
      } else if (id == "jobTitle") {
        setJobTitle(updatedValue);
      } else if (id == "phoneOffice") {
        setPhoneOffice(updatedValue);
      } else if (id == "phoneMobile") {
        setPhoneMobile(updatedValue);
      } else if (id == "phoneOther") {
        setPhoneOther(updatedValue);
      } else if (id == "emailOffice") {
        setEmailOffice(updatedValue);
      } 
    },
    []
  );

  useEffect(() => {
    if (userId) {
      setFirstName(dataBenchmarkSingleContact?.FirstName);
      setLastName(dataBenchmarkSingleContact?.LastName);
      setJobTitle(dataBenchmarkSingleContact?.JobTitle);
      setPhoneOffice(dataBenchmarkSingleContact?.PhoneOffice);
      setPhoneMobile(dataBenchmarkSingleContact?.PhoneMobile);
      setPhoneOther(dataBenchmarkSingleContact?.PhoneOther);
      setEmailOffice(dataBenchmarkSingleContact?.EmailOffice);
      setBilling(dataBenchmarkSingleContact?.Change_BillingContact);
      setPrimary(dataBenchmarkSingleContact?.Change_PrimaryContact);
    }
  }, [dataBenchmarkSingleContact]);

  const handleCheckbox = (id: string, isChecked: boolean) => {
    if (id == "primaryContact") {
      setPrimary(isChecked);
    } else if (id == "billingContact") {
      setBilling(isChecked);
    }
  };

  const handleSubmit = async () => {
    if (firstName === "") {
      setValidationMsg("Please Enter First Name");
    } else if (lastName === "") {
      setValidationMsg("Please Enter Last Name.");
    } else if (phoneOffice === "") {
      setValidationMsg("Please enter Office Phone number.");
    } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(phoneOffice)) {
      setValidationMsg("Please enter a Valid Office Phone number.");
    } else if (
      !/^\(\d{3}\) \d{3}-\d{4}$/.test(phoneMobile) &&
      phoneMobile !== ""
    ) {
      setValidationMsg("Please enter a Valid Mobile Phone number.");
    } else if (emailOffice === "") {
      setValidationMsg("Please enter Office Email.");
    } else if (!emailOffice.trim() || !/\S+@\S+\.\S+/.test(emailOffice)) {
      setValidationMsg("Please enter a Valid Office Email.");
    } 
     else {
      try {
        if (userId) {
          setIsShowLoader(true);
          const updateResult = await updateBenchmarkSingleContact(
            userId,
            firstName,
            lastName,
            jobTitle,
            phoneOffice,
            phoneMobile,
            phoneOther,
            emailOffice,
            isPrimary,
            isBilling
          );
          if (updateResult) {
            Swal.fire({
              customClass: {
                popup: "sweetAlert",
              },
              icon: "success",
              showConfirmButton: false,
              timer: 1500,
              title: "Contact Updated Successfully!",
            });
            handleCancel();
          } else {
            Swal.fire({
              customClass: ".swal2-confirm",
              icon: "warning",
              title: "Failed to Update Contact",
            });
          }
        } else {
          setIsShowLoader(true);
          const addResult = await addBenchmarkSingleContact(
            orgIdNew,
            firstName,
            lastName,
            jobTitle,
            phoneOffice,
            phoneMobile,
            phoneOther,
            emailOffice,
            isPrimary,
            isBilling
          );

          if (addResult) {
            Swal.fire({
              customClass: {
                popup: "sweetAlert",
              },
              icon: "success",
              showConfirmButton: false,
              timer: 1500,
              title: "Contact Added Successfully!",
            });

            handleCancel();
          } else {
            Swal.fire({
              customClass: ".swal2-confirm",
              icon: "warning",
              title: "Failed to Add Contact",
            });
          }
        }
      } catch (error) {
        console.error("Error updating contact:", error);
      } finally {
        setIsShowLoader(false);
      }
    }
  };

  const handleCancel = () => {
    // Clear all fields
    setFirstName("");
    setLastName("");
    setJobTitle("");
    setPhoneOffice("");
    setPhoneMobile("");
    setPhoneOther("");
    setEmailOffice("");
    setIsShowLoader(false);
    setPrimary(false);
    setBilling(false);
    setValidationMsg("");
    // Close the modal
    onClose();
  };
  return (
    <Modal
      show={isOpen}
      label="Contact Info"
      onClose={handleCancel}
      className="custom-modal"
    >
      {validationMsg !== "" ? (
        <div className="text-danger">{validationMsg}</div>
      ) : null}
      <div className="contactModalSection">
        <div className="modalForm">
          {isShowLoader || isFetching ? <Loader /> : null}
          <form>
            {
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    {/* <div className="form-group form-check"> */}
                      <Checkbox
                        id="primaryContact"
                        isChecked={isPrimary}
                        onChange={handleCheckbox}
                      />
                      <label className="formFieldChkLabel">
                        Primary Contact
                      </label>
                      <Checkbox
                        id="billingContact"
                        isChecked={isBilling}
                        onChange={handleCheckbox}
                      />
                      <label className="formFieldChkLabel">
                        Billing Contact
                      </label>
                    {/* </div> */}

                    <div className="form-group">
                      <label className="formFieldLabel">
                        First Name:<span className="text-danger">*</span>
                      </label>
                      <div className="formField">
                        <TextField
                          id="firstName"
                          onChange={onChangeFieldValues}
                          type="text"
                          defaultValue={firstName}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="formFieldLabel">
                        Last Name:<span className="text-danger">*</span>
                      </label>
                      <div className="formField">
                        <TextField
                          id="lastName"
                          onChange={onChangeFieldValues}
                          type="text"
                          defaultValue={lastName}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="formFieldLabel">Job Title:</label>
                      <div className="formField">
                        <TextField
                          id="jobTitle"
                          onChange={onChangeFieldValues}
                          type="text"
                          defaultValue={jobTitle}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                  {/* <label >
                        Primary Contact
                      </label> */}
                    <div className="form-group pt-4"></div>
                  
                    <div className="form-group">
                    <label className="formFieldLabel" style={{ display: "inline-block", whiteSpace: "nowrap" }}>
                    Office Phone:<span className="text-danger">*</span>
                      <span style={{ fontWeight: "normal", marginLeft: "5px", fontSize: "0.85em" }}>
                    (Format: (123) 456-7890)
                      </span>
                    </label>
                      <div className="formField">
                        <TextField
                          id="phoneOffice"
                          onChange={onChangeFieldValues}
                          type="text"
                          defaultValue={phoneOffice}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                    <label className="formFieldLabel" style={{ display: "inline-block", whiteSpace: "nowrap" }}>
                    Mobile Phone:
                      <span style={{ fontWeight: "normal", marginLeft: "5px", fontSize: "0.85em" }}>
                        (Format: (123) 456-7890)
                      </span>
                    </label>
                      <div className="formField">
                        <TextField
                          id="phoneMobile"
                          onChange={onChangeFieldValues}
                          type="text"
                          defaultValue={phoneMobile}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="formFieldLabel">
                        Email Office:<span className="text-danger">*</span>
                      </label>
                      <div className="formField">
                        <TextField
                          id="emailOffice"
                          onChange={onChangeFieldValues}
                          type="text"
                          defaultValue={emailOffice}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="formFieldLabel"></label>
                      <div className="formField actionButtons">
                        <button
                          type="button"
                          className="btn cancelPwdBtn"
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn submitBtn"
                          onClick={handleSubmit}
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default OverviewContactModal;
